import * as React from "react";
import { motion } from "framer-motion";
import { clsx } from "clsx";
import TopBarProgress from "react-topbar-progress-indicator";
import { ModifySnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/modifySnapshotEventDialog";
import { useGetDirectoryUserQuery } from "@/api/users-admin";
import { useContentEditor } from "./hooks/useContentEditor";
import { useTypewriterProgress } from "./hooks/useTypewriterProgress";
import { Header } from "./header";
import { Content } from "./content";
import { Actions, SectionActions } from "./actions";
import { FamilySourceResponses } from "./components/familySourceResponses";
import {
  canEditOutcomeResults,
  isBusinessReportOutcome,
  isFamilySummaryOutcome,
} from "@/modules/snapshots/businessReports.types";
import type { TrafficLightRating } from "@/modules/snapshots/businessReports.types";
import type { FamilySource } from "../types";
import type { BusinessReportOutcomeRowProps } from "./types";

const NO_FAMILY_SOURCES: FamilySource[] = [];

const RAG_CARD_ACCENT_CLASSES: Record<TrafficLightRating, string> = {
  RED: "border-[#e7b4ad] bg-[#fffdfb] before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-[#a53227] before:content-['']",
  AMBER:
    "border-[#e7c48a] bg-[#fffdfb] before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-[#9a6415] before:content-['']",
  GREEN:
    "border-[#b7d7c6] bg-[#fffdfb] before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-[#2d7a4f] before:content-['']",
};

const Component: React.FC<BusinessReportOutcomeRowProps> = ({
  processing,
  content,
  evidences,
  events,
  promptResult,
  businessReportOutcomeResultId,
  businessReportOutcome,
  onPromptTypewriterEnded,
  onScroll,
  outcomeId,
  familySources = NO_FAMILY_SOURCES,
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isCreateSnapshotEventDialogOpen, setIsCreateSnapshotEventDialogOpen] = React.useState(false);

  const { data: editedByUser } = useGetDirectoryUserQuery(promptResult.edited_by_id ?? "", {
    skip: !promptResult.edited_by_id,
  });
  const editorName = editedByUser ? `${editedByUser.name} ${editedByUser.surname}`.trim() : null;

  const {
    isEdit,
    editedContent,
    editedRating,
    isLoading,
    toggleIsEdit,
    setEditedContent,
    setEditedRating,
    handleSave,
    handleCancel,
  } = useContentEditor({
    initialContent: content,
    initialRating: promptResult.rating,
    isTrafficLight: promptResult.traffic_light,
    businessReportOutcomeResultId,
    businessReportOutcomeId: businessReportOutcome.id,
  });

  const { typedContent, progressPercentage } = useTypewriterProgress({
    processing,
    content,
    speed: 5,
    promptResult,
    onPromptTypewriterEnded,
    onScroll,
  });

  React.useEffect(() => {
    if (processing && promptResult.is_date) {
      onPromptTypewriterEnded(promptResult);
    }
  }, [processing, promptResult, onPromptTypewriterEnded]);

  const handleCreateEventClick = () => {
    setIsCreateSnapshotEventDialogOpen(true);
  };

  const isFamilySummary = isFamilySummaryOutcome(businessReportOutcome);

  const ragAccentClass =
    !processing && !isEdit && promptResult.rating ? RAG_CARD_ACCENT_CLASSES[promptResult.rating] : null;

  return (
    <>
      {isLoading && <TopBarProgress />}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={clsx(
          "relative mb-4 overflow-hidden rounded-lg border transition-colors",
          {
            "border-coral/30 bg-[#fffdfb]": processing,
            "border-[#ddd5cb] bg-[#fffdfb] hover:border-[#c9b39c]": !processing && !isEdit && !ragAccentClass,
            "border-coral/40 bg-coral/5": isEdit,
          },
          ragAccentClass,
        )}
      >
        <div className={clsx("px-5 py-4", ragAccentClass && "pl-6")}>
          {/* Header with order badge, title, and status */}
          <Header
            order={promptResult.order}
            title={promptResult.name}
            processing={processing}
            isEdit={isEdit}
            hasContent={!!content || promptResult.relevancy != null}
            hasEvent={events.length > 0}
            rating={promptResult.rating}
            actions={
              !isEdit && canEditOutcomeResults(businessReportOutcome) ? (
                <SectionActions
                  canRetry={!isFamilySummary}
                  processing={processing}
                  isProcessing={isProcessing}
                  businessReportOutcomeId={businessReportOutcome.id}
                  businessReportOutcomeResultId={businessReportOutcomeResultId}
                  onEdit={toggleIsEdit}
                  onSetProcessing={setIsProcessing}
                />
              ) : undefined
            }
          />

          <Content
            content={content}
            relevancy={promptResult.relevancy}
            evidences={evidences}
            snapshotCitations={promptResult.snapshot_citations}
            resultDetails={promptResult.result_details}
            webSearchSources={promptResult.web_search_sources}
            processing={processing}
            isEdit={isEdit}
            isProcessing={isProcessing}
            date={promptResult.date}
            isDate={promptResult.is_date}
            errorMessage={promptResult.error_message}
            editedContent={editedContent}
            editedRating={editedRating}
            isLoading={isLoading}
            typedContent={typedContent}
            progressPercentage={progressPercentage}
            isTrafficLight={promptResult.traffic_light}
            onContentChange={setEditedContent}
            onRatingChange={setEditedRating}
            onSave={handleSave}
            onCancel={handleCancel}
          />
          {!isEdit && (
            <FamilySourceResponses
              sources={familySources}
              businessReportPromptId={promptResult.business_report_prompt_id}
            />
          )}
        </div>
      </motion.div>

      <Actions
        processing={processing}
        isProcessing={isProcessing}
        isEdit={isEdit}
        businessReportOutcomeId={businessReportOutcome.id}
        businessReportOutcomeResultId={businessReportOutcomeResultId}
        outcomeId={outcomeId}
        hasSnapshotCollection={!!businessReportOutcome?.snapshot_collection_id && !isFamilySummary}
        onCreateEventClick={handleCreateEventClick}
        events={events}
        editedAt={promptResult.edited_at}
        editorName={editorName}
        isBusinessReport={isBusinessReportOutcome(businessReportOutcome)}
      />

      {isCreateSnapshotEventDialogOpen && (
        <ModifySnapshotEventDialog
          mode="create"
          open={isCreateSnapshotEventDialogOpen}
          onClose={() => setIsCreateSnapshotEventDialogOpen(false)}
          businessReportOutcomeResultId={promptResult.business_report_outcome_result_id}
          businessReportOutcomeId={outcomeId}
          eventType={promptResult.name}
          eventDate={promptResult.date ? new Date(promptResult.date) : undefined}
        />
      )}
    </>
  );
};

export const BusinessReportOutcomeRow = React.memo(Component);
